import { useEffect } from "react"
import useAuth from "./auth"
import ensureDevice from "./ensure-device"
import { ValidateOTP } from "../api"

// Module-level guard: OTPs are single-use, so we must validate at most once per
// page load — React StrictMode (dev) mounts effects twice, which would consume
// the token on the first call and fail ("otp_already_used") on the second.
let consumed = false

/**
 * Seamless login from a one-time token. When the URL carries `?otp=...` (the
 * mobile app opens the web with it), this validates the token, logs this web
 * device in, sets the user credentials, and reloads to the clean URL so any
 * auth-gated page re-renders authenticated.
 *
 * Mount once at app boot (alongside AldooAPI / AldooRealtime).
 */
export default function useOTPLogin() {
  const [, setAuth] = useAuth()

  useEffect(() => {
    if (typeof window === "undefined") return
    if (consumed) return

    const params = new URLSearchParams(window.location.search)
    const otp = params.get("otp")
    if (!otp) return
    consumed = true

    // Clean URL immediately so the token never lingers / gets re-used on reload.
    params.delete("otp")
    const query = params.toString()
    const cleanUrl =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash

    ;(async () => {
      try {
        const deviceID = await ensureDevice({})
        const res = await ValidateOTP(otp, deviceID)
        console.log("[OTP login] validate:", res?.valid, res?.error || "")

        if (res?.valid && res?.auth_token && res?.user) {
          setAuth({
            email: res.user.email,
            deviceID,
            _id: res.user._id,
            auth_token: res.auth_token,
          })
          // Replace the URL (drop the token) and reload so gated routes pick up
          // the new credentials cleanly.
          window.history.replaceState({}, "", cleanUrl)
          window.location.reload()
          return
        }
      } catch (error) {
        console.warn("[OTP login] failed", error)
      }
      // On failure, still strip the spent token from the URL.
      window.history.replaceState({}, "", cleanUrl)
    })()
  }, [])
}
